import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { FormikProps, useFormik } from 'formik';

import { responseTypes } from '@selfserve/react-utils';

import { IContextData } from '../feature/admin-layout';
import { IUpdateFormikReturn } from '../types';
import {
  LocationCloneState,
  Package,
  Plan,
  usePackageItemCreateRequest,
  usePackageItemListRequest,
  usePackageItemUpdateRequest,
  usePlanItemRequest,
  usePlanItemUpdateRequest,
} from '../data';

type PlanUpdateFormikReturn = IUpdateFormikReturn<Plan | Package>;

export const useProductUpdateFormik = (
  isPackage?: boolean
): PlanUpdateFormikReturn => {
  const { openErrorModal, handleGetCatalogItems } =
    useOutletContext<IContextData>() || {};
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const { product } = (state as LocationCloneState) || {};
  const [disabled, setDisabled] = useState(true);
  const { name } = useParams();
  const {
    data: planItemResponse,
    refetch: fetchPlan,
    isLoading: isPlanLoading,
  } = usePlanItemRequest(name as string, !product && !isPackage);
  const {
    data: packageItemResponse,
    refetch: fetchPackage,
    isLoading: isPackageLoading,
  } = usePackageItemListRequest(name as string, !product && !!isPackage);
  const {
    mutateAsync: updatePlan,
    data: planUpdateResponse,
    isLoading: isPlanUpdateLoading,
  } = usePlanItemUpdateRequest(name as string);
  const {
    mutateAsync: updatePackage,
    data: packageUpdateResponse,
    isLoading: isPackageUpdateLoading,
  } = usePackageItemUpdateRequest(name as string);
  const {
    mutateAsync: createPackage,
    data: packageCreateResponse,
    isLoading: isPackageCreateLoading,
  } = usePackageItemCreateRequest();
  const refetch = useMemo(
    () => (isPackage ? fetchPackage : fetchPlan),
    [fetchPackage, fetchPlan]
  );
  const updateProduct = useMemo(
    () => (isPackage ? updatePackage : updatePlan),
    [updatePackage, updatePlan]
  );
  const createProduct = useMemo(
    () => (isPackage ? createPackage : () => null),
    [createPackage]
  );
  const fetchResponse = useMemo(
    () => (isPackage ? packageItemResponse : planItemResponse),
    [packageItemResponse, planItemResponse]
  );
  const updateResponse = useMemo(
    () => (isPackage ? packageUpdateResponse : planUpdateResponse),
    [packageUpdateResponse, planUpdateResponse]
  );
  const createResponse = useMemo(
    () => (isPackage ? packageCreateResponse : null),
    [isPackageCreateLoading]
  );
  const isFetchLoading = useMemo(
    () => (isPackage ? isPackageLoading : isPlanLoading),
    [isPackageLoading, isPlanLoading]
  );
  const isUpdateLoading = useMemo(
    () => (isPackage ? isPackageUpdateLoading : isPlanUpdateLoading),
    [isPackageUpdateLoading, isPlanUpdateLoading]
  );
  const isCreateLoading = useMemo(
    () => (isPackage ? isPackageCreateLoading : false),
    [isPackageCreateLoading]
  );
  const formik: FormikProps<Plan | Package> = useFormik<Plan | Package>({
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: isPackage
      ? {
        ...(product || (packageItemResponse?.data && packageItemResponse?.data)),
      }
      : {
        license_items: [],
        pricing_info: [],
        ...(planItemResponse?.data && planItemResponse?.data),
      },
    onSubmit: (values) => product ? createPackage(values) : updateProduct(values),
  });

  const disableToggle = useCallback((event?: MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    setDisabled((prevValue) => !prevValue);
  }, []);

  useEffect(() => {
    const { status } = updateResponse || {};
    if (String(status).startsWith(responseTypes.SUCCESS)) {
      refetch();
      handleGetCatalogItems({});
      disableToggle();
      navigate(
        pathname.replace(
          fetchResponse?.data?.name as string,
          updateResponse?.data?.name as string
        ),
        { replace: true },
      );
    } else if (status) {
      const message = updateResponse?.data?.message;
      openErrorModal({ text: message?.[0] || message });
    }
  }, [updateResponse]);

  useEffect(() => {
    const { status } = fetchResponse || {};
    if (status && !String(status).startsWith(responseTypes.SUCCESS)) {
      const message = fetchResponse?.data?.message;
      openErrorModal({ text: message?.[0] || message });
    }
  }, [fetchResponse]);

  useEffect(() => {
    const { status } = createResponse || {};
    if (String(status).startsWith(responseTypes.SUCCESS)) {
      navigate(pathname);
      disableToggle();
    }
    if (status && !String(status).startsWith(responseTypes.SUCCESS)) {
      const message = createResponse?.data?.message;
      openErrorModal({ text: message?.[0] || message });
    }
  }, [createResponse]);

  return {
    formik,
    isLoading: isFetchLoading || isUpdateLoading || isCreateLoading,
    disabled,
    disableToggle,
  };
};
